import { useMemo } from 'react';

import CheckIcon from '@mui/icons-material/Check';
import {
  Avatar,
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material';

import { formatPriceValue } from '@/utils/formatPriceValue';

type ProductTileProps = {
  icon: string;
  title: string;
  shortDescription: string;
  price: string | number;
  currency?: string;
  featuresHeader?: string;
  features: string[];
  btnLabel: string;
  variant: 'light' | 'dark' | 'combined';
  paymentInterval?: string;
  priceAddon?: string;
  onClick: () => void;
  disabled?: boolean;
  isFirstFeatureFromPreviousPlan?: boolean;
};
const ProductTile = ({
  onClick,
  title,
  shortDescription,
  variant,
  featuresHeader,
  features,
  icon,
  btnLabel,
  price,
  currency,
  paymentInterval,
  priceAddon,
  disabled,
  isFirstFeatureFromPreviousPlan
}: ProductTileProps) => {
  const priceWithCurrency = useMemo(() => {
    if (currency) {
      return formatPriceValue(0, Number(price), currency);
    }
    return price;
  }, [price, currency]);
  return (
    <Box
      sx={{
        p: 3,
        px: 2.5,
        backgroundColor: variant === 'dark' ? '#0A0D22' : '#fff',
        border: '1px solid #fff',
        display: 'flex',
        gap: '12px',
        flex: 1,
        flexDirection: 'column',
        color: variant === 'dark' ? 'text.primary' : '#000',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {variant === 'combined' && (
        <Box
          sx={{
            position: 'absolute',
            background:
              'linear-gradient(180deg, rgba(106, 138, 255, 0.10) 0%, rgba(106, 138, 255, 0.02) 100%)',
            aspectRatio: 1,
            width: 'calc(100% + 300px)',
            bottom: 0,
            right: 0,
            border: '1px solid #161E4650',
            borderRadius: '100%',
            transform: 'translate(48%, 5%)',
            zIndex: 0
          }}
        />
      )}
      <Box
        sx={{
          display: 'flex',
          gap: '12px',
          flexDirection: 'column'
        }}
      >
        <Avatar
          alt={title}
          src={icon}
          sx={{
            width: '52px',
            height: '52px',
            background: variant === 'dark' ? 'rgb(232, 78, 15)' : '#0A0D22'
          }}
          variant='square'
        />

        <Typography
          sx={{
            fontSize: '32px',
            my: '12px',
            fontWeight: '600'
          }}
          variant='h3'
        >
          {title}
        </Typography>
        <Typography
          sx={{
            lineHeight: '32px',
            fontSize: '20px',
            color: variant === 'dark' ? '#F8F8F8' : '#797878'
          }}
        >
          {shortDescription}
        </Typography>
        <Box sx={{ mt: '16px' }}>
          <Typography sx={{ fontSize: '48px' }}>
            {priceWithCurrency}{' '}
            <span style={{ fontSize: '20px', color: variant === 'dark' ? '#F8F8F8' : '#797878' }}>
              {paymentInterval}
            </span>
          </Typography>
          <Typography sx={{ fontSize: '20px', color: variant === 'dark' ? '#F8F8F8' : '#797878' }}>
            {priceAddon}
          </Typography>
        </Box>
      </Box>
      <Divider
        sx={{
          backgroundColor: '#E7EBFF'
        }}
        variant='fullWidth'
      />
      <List sx={variant === 'combined' ? { mt: 'auto' } : {}}>
        {featuresHeader && (
          <ListItem sx={{ px: 0 }}>
            <ListItemText
              primary={featuresHeader}
              sx={{ m: 0, fontWeight: 'inherit', fontSize: 'inherit' }}
            />
          </ListItem>
        )}
        {features.map((feature, index) => (
          <ListItem
            key={feature}
            sx={{ alignItems: 'flex-start', px: 0 }}
          >
            {((!isFirstFeatureFromPreviousPlan && index === 0) || index > 0) && (
              <ListItemIcon sx={{ minWidth: '32px' }}>
                <CheckIcon sx={{ color: variant === 'dark' ? '#F8F8F8' : '#0D1228' }} />
              </ListItemIcon>
            )}
            <ListItemText
              primary={feature}
              sx={{ m: 0 }}
            />
          </ListItem>
        ))}
      </List>

      <Box sx={{ height: '10px' }} />
      <Button
        disabled={disabled}
        sx={{
          width: '100%',
          py: '22px',
          fontSize: '24px',
          fontWeight: 600,
          lineHeight: 1,

          mt: 'auto',
          ...(variant === 'light'
            ? {
                borderColor: disabled ? 'rgba(0, 0, 0, 0.12) !important' : '#0D1228',
                color: disabled ? 'rgba(0, 0, 0, 0.26) !important' : '#0D1228'
              }
            : {}),
          ...(variant === 'combined'
            ? {
                borderColor: '#0D1228',
                background: '#0D1228',
                color: '#F8F8F8',
                '&:hover': {
                  backgroundColor: '#0d1228d9'
                }
              }
            : {})
        }}
        variant={variant === 'dark' ? 'contained' : 'outlined'}
        onClick={onClick}
      >
        {btnLabel}
      </Button>
    </Box>
  );
};

export default ProductTile;
